import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import * as teamActions from "../actions/teamsActions";
import * as tournamentActions from "../actions/tournamentActions";
import { authApi } from "../../utils/axios";

const UPDATE_SELECTED_TOURNAMENT_REQUEST =
  "tournament/updateSelectedTournamentRequest";
const UPDATE_SELECTED_TOURNAMENT_SUCCESS =
  "tournament/updateSelectedTournamentSuccess";
const UPDATE_SELECTED_TOURNAMENT_FAILURE =
  "tournament/updateSelectedTournamentFailure";

const initialState = {
  tournaments: [],
  selectedTournament: null,
  selectedCategory: null,
  // { undoable: boolean, swap?: {...} } for the most-recent category swap
  undoableSwap: null,
  status: "idle",
  error: null,
  loading: false,
  totalPages: 0,
  isUpdatingSelected: false,
  updateSelectedError: null,
  creatingTournament: false,
  createTournamentError: null,
  uploadingLogo: false,
  uploadLogoError: null,
  updatingSport: false,
  updateSportError: null,
  numberOfTeams: 0,
  teamStats: null,
  venueLocation: null,
  venueMapLink: "",
  venueType: null,
  venueDetails: null,
  dashboard: null,
  dashboardLoading: false,
  dashboardError: null,
};

export const fetchTournaments = createAsyncThunk(
  "tournaments/fetchTournaments",
  async (
    { organisationId, page = 1, limit = 10, search = "", append = false },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.getTournaments(
        organisationId,
        page,
        limit,
        search
      );
      // Make sure we're returning the results array from the response
      return response.results || response || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTournament = createAsyncThunk(
  "tournament/createTournament",
  async (tournamentData, { getState, rejectWithValue }) => {
    try {
      return await tournamentActions.createTournament(tournamentData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTournament = createAsyncThunk(
  "tournaments/updateTournament",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await tournamentActions.updateTournament(id, updateData);

      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateDefaultSelection = createAsyncThunk(
  "tournaments/updateDefaultSelection",
  async (updateData, { rejectWithValue }) => {
    try {
      const response =
        await tournamentActions.updateDefaultSelection(updateData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTournamentThunk = createAsyncThunk(
  "tournament/deleteTournament",
  async (tournamentId, { rejectWithValue }) => {
    try {
      await tournamentActions.deleteTournament(tournamentId);
      return tournamentId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete tournament"
      );
    }
  }
);

export const updateSelectedTournament = createAsyncThunk(
  "tournaments/updateSelected",
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await tournamentActions.updateSelectedTournament(
        id,
        updateData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update selected tournament"
      );
    }
  }
);

export const getSelectedTournament = createAsyncThunk(
  "tournament/getSelected",
  async (organisationId, { getState, rejectWithValue }) => {
    try {
      const response =
        await tournamentActions.getSelectedTournamentFromApi(organisationId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTournamentDashboard = createAsyncThunk(
  "tournament/fetchDashboard",
  async (tournamentId, { rejectWithValue }) => {
    try {
      return await tournamentActions.getTournamentDashboardFromApi(tournamentId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadTournamentLogo = createAsyncThunk(
  "tournament/uploadLogo",
  async ({ tournamentId, file }, { rejectWithValue }) => {
    try {
      const response = await tournamentActions.uploadTournamentLogo(
        tournamentId,
        file
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTournamentSport = createAsyncThunk(
  "tournaments/updateTournamentSport",
  async ({ tournamentId, updateData }, { rejectWithValue }) => {
    try {
      const response = await tournamentActions.updateTournament(
        tournamentId,
        updateData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createFixture = createAsyncThunk(
  "tournament/createFixture",
  async ({ tournamentId, fixtureData }, { rejectWithValue }) => {
    try {
      return await tournamentActions.createFixture(tournamentId, fixtureData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCustomFixture = createAsyncThunk(
  "tournament/createCustomFixture",
  async ({ tournamentId, fixtureData }, { rejectWithValue }) => {
    try {
      return await tournamentActions.createCustomFixture(tournamentId, fixtureData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const inviteTeam = createAsyncThunk(
  "teams/inviteTeam",
  async (
    { tournamentId, categoryId, teamData },
    { rejectWithValue }
  ) => {
    try {
      const data = await teamActions.inviteTeam(
        tournamentId,
        categoryId,
        teamData
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const autoGenerateFixtures = createAsyncThunk(
  "tournament/autoGenerateFixtures",
  async (payload, { rejectWithValue }) => {
    try {
      return await tournamentActions.autoGenerateFixtures(payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const generateAllFixtures = createAsyncThunk(
  "tournament/generateAllFixtures",
  async (payload, { rejectWithValue }) => {
    try {
      const result = await tournamentActions.generateAllFixtures(payload);
      // API returns { status, data: { tournament, groupedFixtures } } — extract tournament for syncTournamentAndCategory
      return result?.data?.tournament || null;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getTeamStats = createAsyncThunk(
  "tournament/getTeamStats",
  async (
    { tournamentId, categoryId, teamId },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.getTeamStats(
        tournamentId,
        categoryId,
        teamId
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


export const createTournamentPhase = createAsyncThunk(
  "tournament/createPhase",
  async (
    { tournamentId, categoryId, phaseData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.createPhase(
        tournamentId,
        categoryId,
        phaseData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkCreateTournamentPhases = createAsyncThunk(
  "tournament/bulkCreatePhases",
  async (
    { tournamentId, categoryId, payload },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.bulkCreatePhases(
        tournamentId,
        categoryId,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const swapTeamInCategory = createAsyncThunk(
  "tournament/swapTeamInCategory",
  async ({ tournamentId, categoryId, payload }, { rejectWithValue }) => {
    try {
      const response = await tournamentActions.swapTeamInCategory(
        tournamentId,
        categoryId,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUndoableSwap = createAsyncThunk(
  "tournament/fetchUndoableSwap",
  async ({ tournamentId, categoryId }, { rejectWithValue }) => {
    try {
      return await tournamentActions.getUndoableSwap(tournamentId, categoryId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const undoLastSwap = createAsyncThunk(
  "tournament/undoLastSwap",
  async ({ tournamentId, categoryId }, { rejectWithValue }) => {
    try {
      return await tournamentActions.undoLastSwap(tournamentId, categoryId);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTournamentBlock = createAsyncThunk(
  "tournament/createBlock",
  async (
    { tournamentId, categoryId, phaseId, blockData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.createBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const assignTeamsToTournamentBlock = createAsyncThunk(
  "tournament/assignTeamsToBlock",
  async (
    { tournamentId, categoryId, phaseId, blockId, teamsData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.assignTeamsToBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        teamsData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updatePhase = createAsyncThunk(
  "tournament/updatePhase",
  async (
    { tournamentId, categoryId, phaseId, phaseData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.updatePhase(
        tournamentId,
        categoryId,
        phaseId,
        phaseData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const bulkLockPhases = createAsyncThunk(
  "tournament/bulkLockPhases",
  async ({ tournamentId, phases }, { rejectWithValue }) => {
    try {
      return await tournamentActions.bulkLockPhases(tournamentId, phases);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deletePhase = createAsyncThunk(
  "tournament/deletePhase",
  async (
    { tournamentId, categoryId, phaseId },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.deletePhase(
        tournamentId,
        categoryId,
        phaseId
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBlock = createAsyncThunk(
  "tournament/updateBlock",
  async (
    { tournamentId, categoryId, phaseId, blockId, blockData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.updateBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        blockData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBlock = createAsyncThunk(
  "tournament/deleteBlock",
  async (
    { tournamentId, categoryId, phaseId, blockId },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.deleteBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockId
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTeamsInBlock = createAsyncThunk(
  "tournament/updateTeamsInBlock",
  async (
    { tournamentId, categoryId, phaseId, blockId, teamsData },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.updateTeamsInBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        teamsData
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTeamsFromBlock = createAsyncThunk(
  "tournament/deleteTeamsFromBlock",
  async (
    { tournamentId, categoryId, phaseId, blockId, teamIds },
    { rejectWithValue }
  ) => {
    try {
      const response = await tournamentActions.deleteTeamsFromBlock(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        teamIds
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createProgressionRule = createAsyncThunk(
  "tournament/createProgressionRule",
  async (
    { tournamentId, categoryId, phaseId, ruleData },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.createProgressionRule(
        tournamentId,
        categoryId,
        phaseId,
        ruleData
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProgressionRules = createAsyncThunk(
  "tournament/getProgressionRules",
  async (
    { tournamentId, categoryId, phaseId },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.getProgressionRules(
        tournamentId,
        categoryId,
        phaseId
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProgressionRule = createAsyncThunk(
  "tournament/updateProgressionRule",
  async (
    { tournamentId, categoryId, phaseId, ruleId, ruleData },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.updateProgressionRule(
        tournamentId,
        categoryId,
        phaseId,
        ruleId,
        ruleData
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteProgressionRule = createAsyncThunk(
  "tournament/deleteProgressionRule",
  async (
    { tournamentId, categoryId, phaseId, ruleId },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.deleteProgressionRule(
        tournamentId,
        categoryId,
        phaseId,
        ruleId
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const executeProgressionRules = createAsyncThunk(
  "tournament/executeProgressionRules",
  async (
    { tournamentId, categoryId, phaseId, body },
    { rejectWithValue }
  ) => {
    try {
      const result = await tournamentActions.executeProgressionRules(
        tournamentId,
        categoryId,
        phaseId,
        body
      );
      // API returns { tournament, executionSummary } — extract tournament for syncTournamentAndCategory
      return result.tournament || result;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const goLive = createAsyncThunk(
  "tournament/goLive",
  async (payload, { rejectWithValue }) => {
    try {
      return await tournamentActions.goLive(payload);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFixture = createAsyncThunk(
  "tournament/updateFixture",
  async (
    {
      tournamentId,
      categoryId,
      phaseId,
      blockId,
      fixtureId,
      fixtureData,
    },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.updateFixture(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        fixtureId,
        fixtureData
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addFixtureResult = createAsyncThunk(
  "tournament/addFixtureResult",
  async ({ tournamentId, resultData }, { rejectWithValue }) => {
    try {
      return await tournamentActions.addFixtureResult(tournamentId, resultData);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createMatchBreak = createAsyncThunk(
  "tournaments/createMatchBreak",
  async ({ tournamentId, breakData }, { rejectWithValue }) => {
    try {
      const response = await authApi({
        method: "POST",
        url: `/tournaments/${tournamentId}/breaks`,
        data: breakData,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createCategory = createAsyncThunk(
  "tournaments/createCategory",
  async ({ tournamentId, categoryData }, { rejectWithValue }) => {
    try {
      const response = await authApi({
        method: "POST",
        url: `/tournaments/${tournamentId}/categories`,
        data: categoryData,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCategory = createAsyncThunk(
  "tournaments/deleteCategory",
  async ({ tournamentId, categoryId }, { rejectWithValue }) => {
    try {
      const response = await authApi({
        method: "DELETE",
        url: `/tournaments/${tournamentId}/categories/${categoryId}`,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const respondToMemberInvite = createAsyncThunk(
  "tournament/respondToMemberInvite",
  async ({ tournamentId, response }, { rejectWithValue }) => {
    try {
      return await tournamentActions.respondToMemberInvite(tournamentId, response);
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMatchBreak = createAsyncThunk(
  "tournaments/deleteMatchBreak",
  async ({ tournamentId, breakId }, { rejectWithValue }) => {
    try {
      const response = await authApi({
        method: "DELETE",
        url: `/tournaments/${tournamentId}/breaks/${breakId}`,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTeamExtraPoints = createAsyncThunk(
  "tournament/updateTeamExtraPoints",
  async (
    {
      tournamentId,
      categoryId,
      phaseId,
      blockId,
      teamId,
      extraPoints,
    },
    { rejectWithValue }
  ) => {
    try {
      return await tournamentActions.updateTeamExtraPoints(
        tournamentId,
        categoryId,
        phaseId,
        blockId,
        teamId,
        extraPoints
      );
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const tournamentSlice = createSlice({
  name: "tournaments",
  initialState,
  reducers: {
    selectTournament: (state, action) => {
      state.selectedTournament = action.payload;
      state.selectedCategory = null;

      // Auto-select first category if available
      if (action.payload?.categories?.length > 0) {
        state.selectedCategory = action.payload.categories[0];
      }
    },
    clearSelectedTournament: (state) => {
      state.selectedTournament = null;
      state.selectedCategory = null;
    },
    [UPDATE_SELECTED_TOURNAMENT_REQUEST]: (state) => {
      state.isUpdatingSelected = true;
      state.updateSelectedError = null;
    },
    [UPDATE_SELECTED_TOURNAMENT_SUCCESS]: (state, action) => {
      state.isUpdatingSelected = false;
      state.selectedTournament = action.payload;
    },
    [UPDATE_SELECTED_TOURNAMENT_FAILURE]: (state, action) => {
      state.isUpdatingSelected = false;
      state.updateSelectedError = action.payload;
    },
    resetTournamentState: (state) => {
      state.creatingTournament = false;
      state.createTournamentError = null;
    },
    setSelectedTournament: (state, action) => {
      state.selectedTournament = action.payload;
      state.selectedCategory = null;
    },
    updateTournament: (state, action) => {
      const { id, updateData } = action.payload;
      if (state.selectedTournament?.id === id) {
        state.selectedTournament = {
          ...state.selectedTournament,
          ...updateData,
        };
      }
    },
    selectCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearSelectedCategory: (state) => {
      state.selectedCategory = null;
    },
    clearAllSelections: (state) => {
      state.selectedTournament = null;
      state.selectedCategory = null;
      state.tournaments = [];
    },
    updateCategoryLocally: (state, action) => {
      const { tournamentId, categoryId, updatedCategory } = action.payload;

      const selectedTournamentId = (state.selectedTournament?.id || state.selectedTournament?._id)?.toString?.();
      const selectedCategoryId = (state.selectedCategory?._id || state.selectedCategory?.id)?.toString?.();
      const incomingTournamentId = tournamentId?.toString?.();
      const incomingCategoryId = categoryId?.toString?.();

      // Update the selected category directly
      if (selectedCategoryId && incomingCategoryId && selectedCategoryId === incomingCategoryId) {
        state.selectedCategory = updatedCategory;
      }

      // Update the category in the selected tournament
      if (selectedTournamentId && incomingTournamentId && selectedTournamentId === incomingTournamentId) {
        if (Array.isArray(state.selectedTournament?.categories)) {
          state.selectedTournament.categories = state.selectedTournament.categories.map(
            (cat) => {
              const cId = (cat._id || cat.id)?.toString?.();
              return incomingCategoryId && cId === incomingCategoryId
                ? updatedCategory
                : cat;
            }
          );
        }
      }
    },
    setVenueLocation: (state, action) => {
      state.venueLocation = action.payload.location;
      state.venueMapLink = action.payload.mapLink;
    },
    setVenueType: (state, action) => {
      state.venueType = action.payload;
    },
    setVenueDetails: (state, action) => {
      state.venueDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Helper: update tournaments list, selectedTournament, and sync selectedCategory
    const syncTournamentAndCategory = (state, updatedTournament) => {
      if (!updatedTournament) return;
      state.tournaments = state.tournaments.map((tournament) =>
        tournament.id === updatedTournament.id
          ? updatedTournament
          : tournament
      );
      if (state.selectedTournament?.id === updatedTournament.id) {
        state.selectedTournament = updatedTournament;
        // Keep selectedCategory in sync with updated tournament data
        if (state.selectedCategory?._id) {
          const updatedCategory = updatedTournament.categories?.find(
            (cat) => cat._id === state.selectedCategory._id
          );
          if (updatedCategory) {
            state.selectedCategory = updatedCategory;
          }
        }
      }
    };

    builder
      // Fetch tournaments
      .addCase(fetchTournaments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTournaments.fulfilled, (state, action) => {
        state.loading = false;

        // Handle appending results
        if (action.meta.arg.append) {
          state.tournaments = [...state.tournaments, ...action.payload];
        } else {
          state.tournaments = action.payload;
        }

        // Update selected states based on the fetched data
        // Only set selectedTournament from server if none is currently selected in Redux,
        // to avoid overwriting a user's recent selection with stale server data
        const tournaments = Array.isArray(action.payload) ? action.payload : [];

        if (!state.selectedTournament) {
          // Prefer the server-marked default (`isSelected: true`), but fall
          // back to the first tournament so a freshly-logged-in user with
          // accepted tournaments but no current "default" still lands on
          // something instead of a blank dashboard.
          // Skip tournaments where the requesting user's membership is still
          // PENDING — the response includes them so the Invitations panel
          // can render them, but they shouldn't be preselected.
          const selectable = tournaments.filter((t) => t.userMemberStatus !== 'PENDING');
          const selectedTournament = selectable.find((t) => t.isSelected) || selectable[0] || null;

          if (selectedTournament) {
            state.selectedTournament = selectedTournament;

            if (selectedTournament.categories?.length > 0) {
              const currentCategoryId = state.selectedCategory?._id;
              const preserved = currentCategoryId
                ? selectedTournament.categories.find((cat) => cat._id === currentCategoryId)
                : null;
              state.selectedCategory = preserved || selectedTournament.categories[0];
            } else {
              state.selectedCategory = null;
            }
          } else {
            state.selectedTournament = null;
            state.selectedCategory = null;
          }
        } else {
          // Refresh the selected tournament's data from the latest fetch
          const refreshed = tournaments.find((t) => t.id === state.selectedTournament.id);
          if (refreshed) {
            state.selectedTournament = refreshed;

            if (refreshed.categories?.length > 0) {
              const currentCategoryId = state.selectedCategory?._id;
              const preserved = currentCategoryId
                ? refreshed.categories.find((cat) => cat._id === currentCategoryId)
                : null;
              state.selectedCategory = preserved || refreshed.categories[0];
            }
          } else {
            // Tournament no longer exists in fresh data (e.g. user was removed)
            const fallback = tournaments.length > 0 ? tournaments[0] : null;
            state.selectedTournament = fallback;
            state.selectedCategory = fallback?.categories?.[0] || null;
          }
        }
      })
      .addCase(fetchTournaments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        
        // If access denied (403), clear tournaments and selected tournament
        const errorMessage = action.payload?.message || action.payload || '';
        if (errorMessage.includes('Access denied') || errorMessage.includes('403') || action.payload?.status === 403) {
          state.tournaments = [];
          state.selectedTournament = null;
          state.selectedCategory = null;
        }
      })
      // Create tournament
      .addCase(createTournament.pending, (state) => {
        state.creatingTournament = true;
        state.createTournamentError = null;
      })
      .addCase(createTournament.fulfilled, (state, action) => {
        state.creatingTournament = false;
        state.tournaments = [...state.tournaments, action.payload];
      })
      .addCase(createTournament.rejected, (state, action) => {
        state.creatingTournament = false;
        state.createTournamentError = action.payload;
      })
      // Update tournament
      .addCase(updateTournament.fulfilled, (state, action) => {
        const updatedTournament = action.payload;

        // Update in tournaments array
        state.tournaments = state.tournaments.map((tournament) =>
          tournament.id === updatedTournament.id
            ? updatedTournament
            : tournament
        );

        // Update selected states if this tournament is selected
        if (
          updatedTournament.isSelected ||
          state.selectedTournament?.id === updatedTournament.id
        ) {
          state.selectedTournament = updatedTournament;

          // Update selected category if it exists in the updated tournament
          if (state.selectedCategory?._id) {
            const updatedCategory = updatedTournament.categories?.find(
              (cat) => cat._id === state.selectedCategory._id
            );
            state.selectedCategory = updatedCategory || null;
          }
        }
      })
      // Update selected tournament
      .addCase(updateSelectedTournament.pending, (state) => {
        state.isUpdatingSelected = true;
        state.updateSelectedError = null;
      })
      .addCase(updateSelectedTournament.fulfilled, (state, action) => {
        state.isUpdatingSelected = false;
        const updatedTournament = action.payload;

        state.selectedTournament = updatedTournament;

        // Update selected category
        if (state.selectedCategory?._id) {
          const updatedCategory = updatedTournament.categories?.find(
            (cat) => cat._id === state.selectedCategory._id
          );
          state.selectedCategory = updatedCategory || null;
        } else if (updatedTournament.categories?.length > 0) {
          state.selectedCategory = updatedTournament.categories[0];
        }
      })
      .addCase(updateSelectedTournament.rejected, (state, action) => {
        state.isUpdatingSelected = false;
        state.updateSelectedError = action.payload;
      })
      .addCase(getSelectedTournament.fulfilled, (state, action) => {
        const serverTournament =
          action.payload?.data?.tournament ||
          action.payload?.tournament ||
          action.payload?.data ||
          action.payload;

        // If Redux already has a selected tournament and the server returned a
        // different one (stale isSelected flag), keep the Redux selection
        const currentId = state.selectedTournament?.id;
        const serverId = serverTournament?.id;
        if (currentId && serverId && currentId !== serverId) {
          return;
        }

        state.selectedTournament = serverTournament || null;

        const categories = Array.isArray(serverTournament?.categories)
          ? serverTournament.categories
          : [];

        // Preserve currently selected category if it still exists in refreshed data
        const currentCategoryId = state.selectedCategory?._id || state.selectedCategory?.id;
        const matchingCategory = currentCategoryId
          ? categories.find((cat) => (cat._id || cat.id) === currentCategoryId)
          : null;
        state.selectedCategory = matchingCategory || categories[0] || null;
      })
      .addCase(getSelectedTournament.rejected, (state, action) => {
        state.error = action.payload || action.error?.message || state.error;
      })
      .addCase(fetchTournamentDashboard.pending, (state, action) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
        if (state.dashboard?.tournamentId !== action.meta.arg) {
          state.dashboard = null;
        }
      })
      .addCase(fetchTournamentDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboard = { tournamentId: action.meta.arg, ...action.payload };
      })
      .addCase(fetchTournamentDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })
      .addCase(uploadTournamentLogo.pending, (state) => {
        state.uploadingLogo = true;
        state.uploadLogoError = null;
      })
      .addCase(uploadTournamentLogo.fulfilled, (state, action) => {
        state.uploadingLogo = false;
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(uploadTournamentLogo.rejected, (state, action) => {
        state.uploadingLogo = false;
        state.uploadLogoError = action.payload;
      })
      .addCase(updateTournamentSport.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      // Create fixture
      .addCase(createFixture.pending, (state) => {
        state.loading = true;
      })
      .addCase(createFixture.fulfilled, (state, action) => {
        const payload =
          action.payload?.data ||
          action.payload?.result ||
          action.payload?.response ||
          action.payload;

        // Case A: backend returns the updated tournament (sometimes nested or with _id instead of id)
        const maybeTournament =
          payload?.tournament || payload?.updatedTournament || payload;
        const looksLikeTournament =
          maybeTournament && (Array.isArray(maybeTournament?.categories) || Array.isArray(maybeTournament?.divisions));

        if (looksLikeTournament) {
          const updatedTournament = maybeTournament;
          const updatedTournamentId = updatedTournament.id || updatedTournament._id;
          const selectedTournamentId =
            state.selectedTournament?.id || state.selectedTournament?._id;

          // Update in tournaments array (handle id vs _id)
          state.tournaments = state.tournaments.map((tournament) => {
            const tId = tournament.id || tournament._id;
            return tId === updatedTournamentId ? updatedTournament : tournament;
          });

          // Update selected tournament/category by matching ids
          if (selectedTournamentId && selectedTournamentId === updatedTournamentId) {
            state.selectedTournament = updatedTournament;

            const selectedCategoryId = state.selectedCategory?._id || state.selectedCategory?.id;
            if (selectedCategoryId) {
              const updatedCategory = updatedTournament.categories?.find((cat) => {
                const cId = cat._id || cat.id;
                return cId === selectedCategoryId;
              });
              state.selectedCategory = updatedCategory || state.selectedCategory || null;
            }
          }
        }

        // Case B: backend returns an updated category
        const maybeCategory =
          payload?.category ||
          payload?.updatedCategory ||
          (payload?._id && (payload.fixtures || payload.phases) ? payload : null);

        if (maybeCategory && state.selectedCategory?._id === maybeCategory._id) {
          state.selectedCategory = {
            ...state.selectedCategory,
            ...maybeCategory,
          };

          // Sync into selectedTournament
          if (state.selectedTournament?.categories) {
            state.selectedTournament.categories = state.selectedTournament.categories.map((cat) =>
              cat._id === maybeCategory._id ? state.selectedCategory : cat
            );
          }
        }

        // Case C: backend returns the created fixture only (or nested fixture)
        const createdFixture =
          payload?.fixture || payload?.createdFixture || payload?.match || payload;
        const looksLikeFixture =
          createdFixture &&
          (createdFixture.matchTime || createdFixture.eventId) &&
          (createdFixture._id || createdFixture.id);

        if (looksLikeFixture && state.selectedCategory) {
          const newId = createdFixture._id || createdFixture.id;
          const existing = state.selectedCategory.fixtures || [];

          if (!existing.some((f) => (f._id || f.id) === newId)) {
            state.selectedCategory.fixtures = [...existing, createdFixture];
          }

          // Sync with selectedTournament
          if (state.selectedTournament?.categories && state.selectedCategory?._id) {
            state.selectedTournament.categories = state.selectedTournament.categories.map((cat) =>
              cat._id === state.selectedCategory._id ? state.selectedCategory : cat
            );
          }
        }

        state.loading = false;
      })
      .addCase(createFixture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create custom fixture (phase-aware, shares same response handling)
      .addCase(createCustomFixture.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCustomFixture.fulfilled, (state, action) => {
        const payload =
          action.payload?.data ||
          action.payload?.result ||
          action.payload?.response ||
          action.payload;

        const maybeTournament = payload?.tournament || payload?.updatedTournament || payload;
        const looksLikeTournament =
          maybeTournament && (Array.isArray(maybeTournament?.categories) || Array.isArray(maybeTournament?.divisions));

        if (looksLikeTournament) {
          const updatedTournament = maybeTournament;
          const updatedTournamentId = (updatedTournament.id || updatedTournament._id)?.toString?.();
          const selectedTournamentId = (state.selectedTournament?.id || state.selectedTournament?._id)?.toString?.();

          state.tournaments = state.tournaments.map((tournament) => {
            const tId = (tournament?.id || tournament?._id)?.toString?.();
            return tId && tId === updatedTournamentId ? updatedTournament : tournament;
          });

          if (selectedTournamentId && selectedTournamentId === updatedTournamentId) {
            state.selectedTournament = updatedTournament;
            const selectedCategoryId = (state.selectedCategory?._id || state.selectedCategory?.id)?.toString?.();
            if (selectedCategoryId) {
              const updatedCategory = updatedTournament.categories?.find((cat) => {
                const cId = (cat?._id || cat?.id)?.toString?.();
                return cId && cId === selectedCategoryId;
              });
              if (updatedCategory) {
                state.selectedCategory = updatedCategory;
              }
            }
          }
        }

        const maybeCategory =
          payload?.category ||
          payload?.updatedCategory ||
          (payload?._id && (payload.fixtures || payload.phases) ? payload : null);

        if (maybeCategory && state.selectedCategory?._id === maybeCategory._id) {
          state.selectedCategory = { ...state.selectedCategory, ...maybeCategory };
          if (state.selectedTournament?.categories) {
            state.selectedTournament.categories = state.selectedTournament.categories.map((cat) =>
              cat._id === maybeCategory._id ? state.selectedCategory : cat
            );
          }
        }

        const createdFixture = payload?.fixture || payload?.createdFixture || payload?.match || payload;
        const looksLikeFixture =
          createdFixture &&
          (createdFixture.matchTime || createdFixture.eventId) &&
          (createdFixture._id || createdFixture.id);

        if (looksLikeFixture && state.selectedCategory) {
          const newId = createdFixture._id || createdFixture.id;
          const existing = state.selectedCategory.fixtures || [];
          if (!existing.some((f) => (f._id || f.id) === newId)) {
            state.selectedCategory.fixtures = [...existing, createdFixture];
          }
          if (state.selectedTournament?.categories && state.selectedCategory?._id) {
            state.selectedTournament.categories = state.selectedTournament.categories.map((cat) =>
              cat._id === state.selectedCategory._id ? state.selectedCategory : cat
            );
          }
        }

        state.loading = false;
      })
      .addCase(createCustomFixture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(inviteTeam.pending, (state) => {
        state.loading = true;
      })
      .addCase(inviteTeam.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTournament = action.payload;

        // Update in tournaments array
        state.tournaments = state.tournaments.map((tournament) =>
          tournament.id === updatedTournament.id
            ? updatedTournament
            : tournament
        );

        // Update selected tournament if it matches
        if (state.selectedTournament?.id === updatedTournament.id) {
          state.selectedTournament = updatedTournament;

          // Update selected category
          if (state.selectedCategory?._id) {
            const updatedCategory = updatedTournament.categories?.find(
              (cat) => cat._id === state.selectedCategory._id
            );
            if (updatedCategory) {
              state.selectedCategory = updatedCategory;
            }
          }
        }
      })
      .addCase(inviteTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(autoGenerateFixtures.pending, (state) => {
        state.loading = true;
      })
      .addCase(autoGenerateFixtures.fulfilled, (state, action) => {
        state.loading = false;
        const { fixtures } = action.payload;

        if (state.selectedCategory) {
          state.selectedCategory.fixtures = fixtures;

          // Sync with selectedTournament
          if (state.selectedTournament?.categories) {
            state.selectedTournament.categories = state.selectedTournament.categories.map((cat) =>
              cat._id === state.selectedCategory?._id ? state.selectedCategory : cat
            );
          }
        }
      })
      .addCase(autoGenerateFixtures.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(generateAllFixtures.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(getTeamStats.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTeamStats.fulfilled, (state, action) => {
        state.loading = false;
        state.teamOverview = action.payload;
      })
      .addCase(getTeamStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTournamentPhase.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(bulkCreateTournamentPhases.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(swapTeamInCategory.fulfilled, (state, action) => {
        // Use the same payload-unwrap + category-preserve logic as
        // getSelectedTournament.fulfilled, so the swap result lands reliably
        // in selectedTournament AND selectedCategory regardless of how the
        // server wraps the response.
        const serverTournament =
          action.payload?.data?.tournament ||
          action.payload?.tournament ||
          action.payload?.data ||
          action.payload;
        if (!serverTournament) return;

        state.tournaments = state.tournaments.map((t) =>
          (t.id || t._id) === (serverTournament.id || serverTournament._id) ? serverTournament : t
        );
        state.selectedTournament = serverTournament;

        const categories = Array.isArray(serverTournament.categories)
          ? serverTournament.categories
          : [];
        const currentCategoryId = state.selectedCategory?._id || state.selectedCategory?.id;
        const matchingCategory = currentCategoryId
          ? categories.find((cat) => (cat._id || cat.id) === currentCategoryId)
          : null;
        state.selectedCategory = matchingCategory || categories[0] || null;
      })
      .addCase(fetchUndoableSwap.fulfilled, (state, action) => {
        state.undoableSwap = action.payload || { undoable: false };
      })
      .addCase(fetchUndoableSwap.rejected, (state) => {
        state.undoableSwap = { undoable: false };
      })
      .addCase(undoLastSwap.fulfilled, (state, action) => {
        // Same payload-unwrap as swapTeamInCategory.fulfilled — the undo returns
        // the populated tournament. Then the swap is no longer undoable.
        const serverTournament =
          action.payload?.data?.tournament ||
          action.payload?.tournament ||
          action.payload?.data ||
          action.payload;
        if (serverTournament) {
          state.tournaments = state.tournaments.map((t) =>
            (t.id || t._id) === (serverTournament.id || serverTournament._id) ? serverTournament : t
          );
          state.selectedTournament = serverTournament;
          const categories = Array.isArray(serverTournament.categories)
            ? serverTournament.categories
            : [];
          const currentCategoryId = state.selectedCategory?._id || state.selectedCategory?.id;
          const matchingCategory = currentCategoryId
            ? categories.find((cat) => (cat._id || cat.id) === currentCategoryId)
            : null;
          state.selectedCategory = matchingCategory || categories[0] || null;
        }
        state.undoableSwap = { undoable: false };
      })
      .addCase(createTournamentBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(assignTeamsToTournamentBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(updatePhase.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(bulkLockPhases.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deletePhase.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(updateBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(updateTeamsInBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteTeamsFromBlock.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(createProgressionRule.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(getProgressionRules.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(updateProgressionRule.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteProgressionRule.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(executeProgressionRules.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteTournamentThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTournamentThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted tournament from the tournaments array
        state.tournaments = state.tournaments.filter(
          (tournament) => tournament.id !== action.payload
        );
        // Select another tournament if the deleted one was selected
        if (state.selectedTournament?.id === action.payload) {
          const fallback = state.tournaments.length > 0 ? state.tournaments[0] : null;
          state.selectedTournament = fallback;
          state.selectedCategory = fallback?.categories?.[0] || null;
        }
      })
      .addCase(deleteTournamentThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(goLive.pending, (state) => {
        state.loading = true;
      })
      .addCase(goLive.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(goLive.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateFixture.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateFixture.fulfilled, (state, action) => {
        state.loading = false;
        const tournament = action.payload;
        if (!tournament) return;

        // Normalise id comparisons — depending on the API path, a tournament
        // arrives with either `id` (top-level docs go through toJSON) or
        // `_id` (raw mongoose subdoc shape). Nested categories use `_id`.
        // Comparing strings unconditionally avoids reference-equality traps.
        const tId = (tournament.id || tournament._id)?.toString?.();

        state.tournaments = state.tournaments.map((t) => {
          const existingId = (t?.id || t?._id)?.toString?.();
          return existingId && existingId === tId ? tournament : t;
        });

        const selectedTournamentId = (
          state.selectedTournament?.id || state.selectedTournament?._id
        )?.toString?.();
        if (selectedTournamentId && selectedTournamentId === tId) {
          state.selectedTournament = tournament;

          const selectedCategoryId = (
            state.selectedCategory?._id || state.selectedCategory?.id
          )?.toString?.();
          if (selectedCategoryId) {
            const updatedCategory = tournament.categories?.find((cat) => {
              const cId = (cat?._id || cat?.id)?.toString?.();
              return cId && cId === selectedCategoryId;
            });
            if (updatedCategory) {
              state.selectedCategory = updatedCategory;
            }
          }
        }
      })
      .addCase(updateFixture.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addFixtureResult.pending, (state) => {
        state.loading = true;
      })
      .addCase(addFixtureResult.fulfilled, (state, action) => {
        state.loading = false;

        // Backend returns populated tournament with updated fixture data
        const updatedTournament = action.payload;

        if (updatedTournament?.id || updatedTournament?._id) {
          const tournamentId = updatedTournament.id || updatedTournament._id;

          // Update tournaments list
          state.tournaments = state.tournaments.map((tournament) =>
            (tournament.id || tournament._id) === tournamentId ? updatedTournament : tournament
          );

          // Update selected tournament if it matches
          const selectedTournamentId = state.selectedTournament?.id || state.selectedTournament?._id;
          if (selectedTournamentId === tournamentId) {
            state.selectedTournament = updatedTournament;

            // Find and update selected category
            const selectedCategoryId = state.selectedCategory?._id || state.selectedCategory?.id;
            if (selectedCategoryId) {
              const updatedCategory = updatedTournament.categories?.find(
                (cat) => (cat._id || cat.id) === selectedCategoryId
              );
              if (updatedCategory) {
                state.selectedCategory = updatedCategory;
              }
            }
          }
        }

        // Optimistic update: ensure the specific fixture reflects what was saved,
        // in case the backend response doesn't include updated matchDetails.
        const { resultData } = action.meta.arg;
        const fixtureId = resultData?.fixture?._id;
        if (fixtureId && state.selectedCategory) {
          const applyToFixtures = (fixtures) =>
            (fixtures || []).map((f) => {
              if ((f._id || f.id) !== fixtureId) return f;
              return {
                ...f,
                matchDetails: resultData.matchDetails ?? f.matchDetails,
                homeTeamScore: resultData.teamScores?.homeTeamScore ?? f.homeTeamScore,
                awayTeamScore: resultData.teamScores?.awayTeamScore ?? f.awayTeamScore,
                hasResult: true,
              };
            });

          state.selectedCategory.phases = (state.selectedCategory.phases || []).map((phase) => ({
            ...phase,
            blocks: (phase.blocks || []).map((block) => ({
              ...block,
              fixtures: applyToFixtures(block.fixtures),
              rounds: (block.rounds || []).map((round) => ({
                ...round,
                fixtures: applyToFixtures(round.fixtures),
              })),
            })),
          }));
        }
      })
      .addCase(addFixtureResult.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMatchBreak.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(deleteMatchBreak.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(updateTeamExtraPoints.fulfilled, (state, action) => {
        syncTournamentAndCategory(state, action.payload);
      })
      .addCase(respondToMemberInvite.fulfilled, (state, action) => {
        // Mark the invitation as responded in local state
        // The DashboardLayout will refetch tournaments to get the full updated data
      });
  },
});

export const {
  selectTournament,
  clearSelectedTournament,
  resetTournamentState,
  selectCategory,
  clearSelectedCategory,
  clearAllSelections,
  updateCategoryLocally,
  setVenueLocation,
  setVenueType,
  setVenueDetails,
} = tournamentSlice.actions;
export default tournamentSlice.reducer;

export {
  UPDATE_SELECTED_TOURNAMENT_FAILURE,
  UPDATE_SELECTED_TOURNAMENT_REQUEST,
  UPDATE_SELECTED_TOURNAMENT_SUCCESS,
};

export const selectSelectedCategory = createSelector(
  (state) => state.tournament.selectedCategory,
  (selectedCategory) => selectedCategory
);

export const selectNumberOfTeams = createSelector(
  (state) => state.tournament.numberOfTeams,
  (numberOfTeams) => numberOfTeams
);
